import { useEffect, useState } from "react";
import { MermaidDiagram, mermaidDiagramKind } from "@strivelabs/ui-blocks";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { useTrack } from "@/lib/analytics/useTrack";

/** How long a fence must stop changing before it is drawn. */
export const MERMAID_FENCE_SETTLE_MS = 600;

type HastNode = {
  type?: string;
  tagName?: string;
  value?: string;
  properties?: { className?: unknown };
  children?: HastNode[];
};

/**
 * Extract the source of a ```mermaid fence from the hast `pre` node that
 * react-markdown hands its `pre` component.
 *
 * @param node - The `pre` element's hast node.
 * @returns The fence text, or null when this `pre` is not a mermaid fence.
 */
export function mermaidFenceSource(node: unknown): string | null {
  const code = (node as HastNode | undefined)?.children?.[0];
  if (!code || code.type !== "element" || code.tagName !== "code") return null;
  const className = code.properties?.className;
  const classes = Array.isArray(className)
    ? className
    : typeof className === "string" ? className.split(/\s+/) : [];
  if (!classes.includes("language-mermaid")) return null;
  return (code.children ?? [])
    .map((child) => (child.type === "text" ? child.value ?? "" : ""))
    .join("")
    .trim();
}

/**
 * The safety net for a model that writes a ```mermaid fence instead of
 * calling `ui_mermaid_diagram` (the prompt forbids it, but it happens). It
 * renders through the same themed component, with two differences from the
 * tool path: nothing validated this source server-side, so a failure falls
 * back to the raw text rather than vanishing; and the text streams in chunk
 * by chunk, so drawing waits until it has stopped changing — otherwise every
 * partial chunk would flash a parse failure.
 */
export function MermaidFence({ source }: { source: string }) {
  const trackEvent = useTrack();
  const [settled, setSettled] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(source), MERMAID_FENCE_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [source]);

  if (settled !== source) {
    return (
      <div
        className="my-2 h-40 w-full animate-pulse rounded-lg border border-custom-border bg-custom-bg-primary"
        role="status"
        aria-label="Drawing diagram"
      />
    );
  }

  const kind = mermaidDiagramKind(source);
  return (
    <MermaidDiagram
      source={source}
      onInvalid="source"
      onRenderError={() => {
        trackEvent(ANALYTICS_EVENTS.CHAT_DIAGRAM_RENDER_FAILED, { via: "fence", kind });
      }}
      onAction={(action) => {
        trackEvent(ANALYTICS_EVENTS.CHAT_DIAGRAM_USED, { action, via: "fence", kind });
      }}
    />
  );
}
